import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import L from "leaflet";
import { MapPin, Heart } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const DACH_CENTER = [48.5, 10.5];
const DACH_ZOOM = 5;

/**
 * HerzMap — vanilla Leaflet (not react-leaflet) to avoid React 18 StrictMode
 * "Map container is already initialized" issues. We manage the Leaflet lifecycle
 * directly via refs and ensure full cleanup on unmount.
 */
export default function HerzMap() {
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markersLayerRef = useRef(null);
  const [acts, setActs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch acts
  useEffect(() => {
    axios
      .get(`${API}/api/herz/map?limit=300`)
      .then((r) => setActs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setActs([]))
      .finally(() => setLoading(false));
  }, []);

  // Initialize map (once)
  useEffect(() => {
    if (!mapDivRef.current || mapRef.current) return;
    const map = L.map(mapDivRef.current, {
      center: DACH_CENTER,
      zoom: DACH_ZOOM,
      scrollWheelZoom: false,
      attributionControl: true,
      zoomControl: true,
    });
    L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
      {
        attribution:
          '© <a href="https://www.openstreetmap.org/copyright">OSM</a> · © <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: "abcd",
        maxZoom: 19,
      }
    ).addTo(map);
    markersLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    return () => {
      try {
        map.remove();
      } catch (_) { /* noop */ }
      mapRef.current = null;
      markersLayerRef.current = null;
    };
  }, []);

  // Render / update markers when acts change
  useEffect(() => {
    const layer = markersLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    acts.forEach((a) => {
      if (typeof a.lat !== "number" || typeof a.lng !== "number") return;
      const m = L.circleMarker([a.lat, a.lng], {
        radius: 9,
        color: "#D4AF37",
        fillColor: "#FFE9A8",
        fillOpacity: 0.85,
        weight: 2,
        className: "herz-map-pulse",
      });
      const where = a.location ? `<span style="font-size:11px;color:#666;"> · ${escapeHtml(a.location)}</span>` : "";
      m.bindPopup(
        `<div style="min-width:220px;color:#1A0C12;font-family:Georgia,serif;">
           <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
             <span style="display:inline-block;width:14px;height:14px;background:#7A1F2B;border-radius:50%;"></span>
             <strong style="font-size:15px;">${escapeHtml(a.actor_name || "Anonym")}</strong>${where}
           </div>
           <p style="font-size:13px;line-height:1.5;margin:0;color:#333;">${escapeHtml(a.text || "")}</p>
         </div>`
      );
      m.addTo(layer);
    });
  }, [acts]);

  return (
    <section className="py-20 border-t border-[#F7EEDD]/5" data-testid="herz-map-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-3 inline-flex items-center gap-2">
            <MapPin size={12} /> Herzkarte
          </div>
          <h2 className="serif text-3xl md:text-5xl font-light leading-tight">
            Wo Herz die Welt berührt.
          </h2>
          <p className="text-[#F7EEDD]/55 italic mt-4 max-w-2xl mx-auto">
            Jeder Goldpunkt ist eine Tat. Klicke darauf, um die Geschichte zu lesen.
          </p>
        </div>

        <div className="relative rounded-3xl overflow-hidden border-2 border-[#D4AF37]/30 shadow-2xl"
             style={{ background: "#0a0508" }} data-testid="herz-map-container">
          <div ref={mapDivRef} style={{ height: 560, width: "100%", background: "#0a0508" }} />

          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm pointer-events-none">
              <div className="text-[#D4AF37] text-sm tracking-widest uppercase">Karte lädt …</div>
            </div>
          )}

          {!loading && acts.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center px-8">
                <Heart size={32} className="mx-auto text-[#D4AF37] fill-[#D4AF37] mb-3 opacity-60" />
                <div className="text-[#F7EEDD] serif text-xl">Noch keine Taten auf der Karte.</div>
                <div className="text-[#F7EEDD]/55 text-sm mt-2">Sei der erste Goldpunkt.</div>
              </div>
            </div>
          )}

          <div className="absolute top-4 right-4 px-4 py-2 rounded-full backdrop-blur-md border z-[400]"
               style={{ background: "rgba(26,12,18,0.7)", borderColor: "#D4AF37" }}
               data-testid="herz-map-count">
            <span className="text-[#D4AF37] serif text-lg">{acts.length}</span>
            <span className="text-[#F7EEDD]/65 text-xs ml-2 tracking-wider uppercase">Taten</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
